import isNull from 'lodash/isNull';
import { FormattedMessage } from 'react-intl';

import { get } from 'utils/lodash';
import { findArrayEntry } from 'utils/array';
import { formatArrayKeys } from 'utils/formatter';
import { getOnetimeLabel, getExchangeRatesDisplay, getDisabledAfterDate, getDisabledBeforeDate } from 'utils/transaction';
import { getRtgsDetail, getRtgsFavouriteDetail, getSknTransfeFavouriteDetail, getSknTransferDetail } from 'middleware/actions/transfer';

import { TODAY } from 'settings/constants/calendar';
import { CURRENCY_IDR, DEFAULT_INT_ZERO, MULTICURRENCY_PRODUCT_TYPE } from 'settings/constants/common';
import { DEFAULT_MIN_TRANSFER_AMOUNT, DEFAULT_MAX_TRANSFER_AMOUNT, TRANSFER_RECURRING_WEEKLY } from 'settings/constants/transaction';

export const getSknRtgsDynamicFields = (detail, senderAccount, isFavouriteTransfer, isRtgsTransfer) => {
  const isRecurring = get(detail, 'isRecurring', false);
  const isTransferNowBlocked = get(detail, 'isTransferNowBlocked', false);
  const initialDate = get(detail, 'initialTransferDate', TODAY);

  const duplicateDateValue = get(detail, 'transferDate', '');
  const transferFrequency = get(detail, 'transferFrequency', '');
  const sourceOfFunds = get(detail, 'sourceOfFunds', []);
  const transactionPurposes = get(detail, 'transactionPurposes', []);
  const beneficiaryTypes = get(detail, 'beneficiaryTypes', []);
  const beneficiaryCitizenStatus = get(detail, 'beneficiaryCitizenTypes', []);
  const holidays = get(detail, 'holidays', []);
  const disabledStartDate = isTransferNowBlocked ? initialDate : '';
  const toAccountEmailAddress = get(detail, 'toAccountEmailAddress', '');

  const minAmount = get(detail, 'serviceInfoBean.minLimit', DEFAULT_INT_ZERO);
  const maxAmount = get(detail, 'serviceInfoBean.maxLimit', DEFAULT_INT_ZERO);
  const currentBalance = get(senderAccount, 'plainBalance', DEFAULT_INT_ZERO);
  const currency = get(senderAccount, 'currency', CURRENCY_IDR);

  const disabledAfterDate = getDisabledAfterDate(isRecurring);
  const disabledBeforeDate = (isEndDate = true) =>
    getDisabledBeforeDate({ isRecurring, disabledStartDate, isEndDate, duplicateDateValue, initialDate });
  const debitLabel = transferFrequency === TRANSFER_RECURRING_WEEKLY ? 'transaction.transfer.debitDay' : 'transaction.transfer.debitDate';

  const disabledEmail = () => {
    if (!isFavouriteTransfer) return false;
    return toAccountEmailAddress;
  };

  return [
    {
      name: 'accountNumber',
      readOnly: isFavouriteTransfer,
    },
    {
      name: 'beneficiaryName',
      readOnly: isFavouriteTransfer,
    },
    // for favourite transfer, if email is blank, the field is not disabled and user can input an email
    {
      name: 'beneficiaryEmail',
      readOnly: disabledEmail(),
    },
    {
      name: 'transferAmount',
      validationRules: [
        { type: 'required', isValidateRequired: true },
        { type: 'minAmount', value: minAmount > DEFAULT_INT_ZERO ? minAmount : DEFAULT_MIN_TRANSFER_AMOUNT, isValidateRequired: true },
        {
          type: 'maxAmount',
          value: maxAmount > DEFAULT_MIN_TRANSFER_AMOUNT ? maxAmount : DEFAULT_MAX_TRANSFER_AMOUNT,
          isValidateRequired: true,
        },
        {
          type: 'senderBalance',
          value: currentBalance,
          isValidateRequired: currency === CURRENCY_IDR, // is same currency? yes : no;
        },
      ],
    },
    { name: 'sourceOfFund', options: sourceOfFunds },
    { name: 'transactionPurpose', options: transactionPurposes },
    { name: 'beneficiaryType', options: beneficiaryTypes },
    {
      name: 'transferDate',
      blockedDays: holidays,
      initialDate,
      disabledAfterDate,
      disabledBeforeDate: disabledBeforeDate(false),
    },
    {
      name: 'transferEndDate',
      blockedDays: holidays,
      duplicateDateValue,
      disabledAfterDate,
      disabledBeforeDate: disabledBeforeDate(),
    },
    {
      name: 'debitDate',
      label: debitLabel,
    },
    { name: 'beneficiaryCitizenStatus', options: beneficiaryCitizenStatus },
  ];
};

export const getSknRtgsBeneficiaryDetails = ({ confirmationDetail, referenceNumber, isResultSuccess, isRecurring }) => {
  const dateLabel = getOnetimeLabel(confirmationDetail);
  const details = [
    { title: <FormattedMessage id="transaction.transfer.beneficiary.bank" />, info: get(confirmationDetail, 'bankName', '') },
    { title: <FormattedMessage id="transaction.transfer.beneficiary.name" />, info: get(confirmationDetail, 'toAccountHolderName', '') },
    { title: <FormattedMessage id="transaction.transfer.sourceOfFund" />, info: get(confirmationDetail, 'sumberDanaDisplay', '') },
    {
      title: <FormattedMessage id="transaction.transfer.transactionPurpose" />,
      info: get(confirmationDetail, 'tujuanTransaksiDisplay', ''),
    },
    { title: <FormattedMessage id="transaction.transfer.beneficiary.type" />, info: get(confirmationDetail, 'jenisPenerimaDisplay', '') },
    {
      title: <FormattedMessage id="transaction.transfer.beneficiary.citizenStatus" />,
      info: get(confirmationDetail, 'statusKependudukanPenerimaDisplay', ''),
    },
    !isRecurring
      ? {
          title: <FormattedMessage id="transaction.transfer.effectiveDate" />,
          info: (
            <span>
              {dateLabel}
              <span className="gray pl1">{get(confirmationDetail, 'effectiveDateDisplay', '')}</span>
            </span>
          ),
        }
      : {},
    { title: <FormattedMessage id="transaction.transfer.message" />, info: get(confirmationDetail, 'message', '-') },
    {
      title: <FormattedMessage id="transaction.transfer.beneficiary.email" />,
      info: get(confirmationDetail, 'toAccountEmailAddress', '-'),
    },
  ].filter(value => Object.keys(value).length);

  if (isResultSuccess)
    return [...details, { title: <FormattedMessage id="transaction.transfer.referenceNumber" />, info: referenceNumber }];
  return details;
};

export const getSknRtgsMonetaryDetails = confirmationDetail => {
  const exchangeRates = get(confirmationDetail, 'exchangeRate', []);
  const exchangeRateDisplay = getExchangeRatesDisplay(exchangeRates, 'transaction.transfer.exchangeRate');

  const details = [
    { title: <FormattedMessage id="transaction.transfer.transactionAmount" />, info: get(confirmationDetail, 'toAmountDis', '') },
    { title: <FormattedMessage id="transaction.transfer.transactionFee" />, info: get(confirmationDetail, 'chargeAmountDis', '') },
    { title: <FormattedMessage id="transaction.transfer.totalDebit" />, info: get(confirmationDetail, 'debitAmountDis', '') },
    { title: <FormattedMessage id="transaction.transfer.localDebitedAmount" />, info: get(confirmationDetail, 'localAmountDis', '') },
  ];

  return details.concat(exchangeRateDisplay);
};

export const getSknRtgsFavouriteDynamicFields = (detail, isFromTransferResult) => {
  const banks = get(detail, 'bankList', []);
  const accountLimits = get(detail, 'favouriteLimitList', []);

  return isFromTransferResult
    ? [
        {
          options: formatArrayKeys(accountLimits),
          name: 'accountLimit',
        },
      ]
    : [
        {
          options: formatArrayKeys(banks),
          name: 'bankCode',
        },
        {
          options: formatArrayKeys(accountLimits),
          name: 'accountLimit',
        },
      ];
};

export const getSknRtgsFavouriteTransferDetail = (detail, isEditFavourite, formDetail) => {
  const bankCode = get(detail, 'bankCode', '');
  const bankName = get(detail, 'bankName', '');
  const toAccountNo = get(detail, 'toAccountNo', '');
  const accountHolderName = get(detail, 'accountHolderName', '');
  const accountNickname = get(detail, 'accountNickname', '');
  const emailAddress = get(detail, 'emailAddress', '') || get(formDetail, 'beneficiaryEmail', '');
  const accountLimit = get(detail, 'accountLimit', '');

  return {
    bankCode,
    bankName,
    toAccountNo,
    accountHolderName,
    emailAddress,
    ...(isEditFavourite && {
      accountNickname,
      accountLimit,
    }),
  };
};

export const getSknRtgsFavouriteBeneficiaryDetails = detail => [
  {
    title: <FormattedMessage id="transaction.favourite.form.beneficiaryNickname" />,
    info: get(detail, 'accountNickname', '-'),
  },
  {
    title: <FormattedMessage id="transaction.transfer.beneficiary.bank" />,
    info: get(detail, 'bankName', '-'),
  },
  {
    title: <FormattedMessage id="transaction.favourite.form.dailyTransactionLimit" />,
    info: get(detail, 'accountLimitDis', '-'),
  },
  {
    title: <FormattedMessage id="transaction.transfer.beneficiary.email" />,
    info: get(detail, 'emailAddress', '-'),
  },
];

export const getSknRtgsEditFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  accountNickname: get(detail, 'confirmationDetail.accountNickname', ''),
  accountHolderName: get(detail, 'confirmationDetail.accountHolderName', ''),
  accountLimit: get(detail, 'confirmationDetail.accountLimit', ''),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  bankCode: get(detail, 'confirmationDetail.bankCode', ''),
  emailAddress: get(detail, 'confirmationDetail.emailAddress', ''),
});

export const getSknRtgsDeleteFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  bankCode: get(detail, 'confirmationDetail.bankCode', ''),
});

export const getSknDetailDispatcher = ({ userId, favouriteIndex, favouriteAccount, isSenderIDR, productCode, dispatch }) => {
  const favAccountMaps = get(favouriteAccount, 'favouriteAccountMap', {});

  if (MULTICURRENCY_PRODUCT_TYPE.includes(productCode) && !isSenderIDR) return getSknTransferDetail();

  return !isNull(favouriteIndex)
    ? dispatch(getSknTransfeFavouriteDetail({ userId, favIndex: favouriteIndex, favAccountMaps }))
    : dispatch(getSknTransferDetail({ userId }));
};

export const getRtgsDetailDispatcher = ({ userId, favouriteIndex, favouriteAccount, isSenderIDR, productCode, dispatch }) => {
  const favAccountMaps = get(favouriteAccount, 'favouriteAccountMap', {});

  if (MULTICURRENCY_PRODUCT_TYPE.includes(productCode) && !isSenderIDR) return getRtgsDetail();

  return !isNull(favouriteIndex)
    ? dispatch(getRtgsFavouriteDetail({ userId, favIndex: favouriteIndex, favAccountMaps }))
    : dispatch(getRtgsDetail({ userId }));
};
